import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { ERP_REITER_IDS, istErpSystem, HAENDLER_STATUS, KAMPAGNE_TYP_LISTE, PRODUKT_STATUS, type ErpReiter, type HaendlerStatus, type Me, type KampagneTyp, type ProduktStatus } from '@dxl/shared';
import { darfPlattform, einrichtungAbfrage, meAbfrage } from './lib/sitzung';
import { portalMeAbfrage } from './lib/portal';
import { Shell } from './components/Shell';
import { navigationFuer, navZiel, PLATTFORM_EINSTELLUNGEN } from './lib/navigation';
import { SeitenFehler } from './components/SeitenFehler';

const EINSTELLUNG_BEREICHE_SUCHE = [
  'stammdaten', 'app', 'ki', 'branding', 'rechtstexte', 'retouren', 'email', 'backup',
  ...PLATTFORM_EINSTELLUNGEN.map((b) => b.id),
] as const;
type EinstellungBereichSuche = 'stammdaten' | 'app' | 'ki' | 'branding' | 'rechtstexte' | 'retouren' | 'email' | 'backup' | (typeof PLATTFORM_EINSTELLUNGEN)[number]['id'];

interface Kontext {
  queryClient: QueryClient;
}

const root = createRootRouteWithContext<Kontext>()({
  component: Outlet,
  notFoundComponent: () => (
    <div className="seite"><h1>Seite nicht gefunden</h1></div>
  ),
});

/* ---------------------------------------------------------------- öffentlich */

const einrichtung = createRoute({
  getParentRoute: () => root,
  path: '/einrichtung',
  beforeLoad: async ({ context }) => {
    const { offen } = await context.queryClient.ensureQueryData(einrichtungAbfrage);
    if (!offen) throw redirect({ to: '/anmelden' });
  },
  component: lazyRouteComponent(() => import('./routes/Einrichtung'), 'Einrichtung'),
});

async function nichtAngemeldet(qc: QueryClient) {
  const { offen } = await qc.ensureQueryData(einrichtungAbfrage);
  if (offen) throw redirect({ to: '/einrichtung' });
  const me = await qc.ensureQueryData(meAbfrage);
  if (me?.stufe === 'voll') throw redirect({ to: '/' });
}

const AnmeldenSeite = lazyRouteComponent(() => import('./routes/Anmelden'), 'Anmelden');

const anmelden = createRoute({
  getParentRoute: () => root,
  path: '/anmelden',
  beforeLoad: ({ context }) => nichtAngemeldet(context.queryClient),
  component: AnmeldenSeite,
});

const mandantAnmelden = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/anmelden',
  beforeLoad: ({ context }) => nichtAngemeldet(context.queryClient),
  component: function MandantAnmelden() {
    const { kuerzel } = mandantAnmelden.useParams();
    return <AnmeldenSeite kuerzel={kuerzel} />;
  },
});

const VergessenSeite = lazyRouteComponent(() => import('./routes/PasswortVergessen'), 'PasswortVergessen');

const passwortVergessen = createRoute({
  getParentRoute: () => root,
  path: '/passwort-vergessen',
  beforeLoad: ({ context }) => nichtAngemeldet(context.queryClient),
  component: VergessenSeite,
});

const mandantPasswortVergessen = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/passwort-vergessen',
  beforeLoad: ({ context }) => nichtAngemeldet(context.queryClient),
  component: function MandantVergessen() {
    const { kuerzel } = mandantPasswortVergessen.useParams();
    return <VergessenSeite kuerzel={kuerzel} />;
  },
});

/** Marktplatz selbst registrieren (Double-Opt-in) – ohne Paketauswahl */
const registrieren = createRoute({
  getParentRoute: () => root,
  path: '/registrieren',
  // ?produkt=werbung: Shop nur mit Retail Media (RM-8), Einstieg von der Seite /retail-media
  validateSearch: (s: Record<string, unknown>): { produkt?: 'werbung' } => (s.produkt === 'werbung' ? { produkt: 'werbung' } : {}),
  beforeLoad: ({ context }) => nichtAngemeldet(context.queryClient),
  component: lazyRouteComponent(() => import('./routes/Registrieren'), 'Registrieren'),
});

/** Öffentliche Vermarktungsseite Retail Media ohne Marktplatz (RM-8) – ohne Anmeldung */
const retailMedia = createRoute({
  getParentRoute: () => root,
  path: '/retail-media',
  component: lazyRouteComponent(() => import('./routes/RetailMedia'), 'RetailMedia'),
});

/** Unterseiten der Vermarktungsseite Retail Media – ohne Anmeldung */
const RetailMediaSeiten = () => import('./routes/RetailMediaSeiten');
const retailMediaFormate = createRoute({ getParentRoute: () => root, path: '/retail-media/formate', component: lazyRouteComponent(RetailMediaSeiten, 'RetailMediaFormate') });
const retailMediaMarken = createRoute({ getParentRoute: () => root, path: '/retail-media/marken', component: lazyRouteComponent(RetailMediaSeiten, 'RetailMediaMarken') });
const retailMediaHaendler = createRoute({ getParentRoute: () => root, path: '/retail-media/haendler', component: lazyRouteComponent(RetailMediaSeiten, 'RetailMediaHaendler') });
const retailMediaAuktion = createRoute({ getParentRoute: () => root, path: '/retail-media/auktion', component: lazyRouteComponent(RetailMediaSeiten, 'RetailMediaAuktion') });
const retailMediaEinbindung = createRoute({ getParentRoute: () => root, path: '/retail-media/einbindung', component: lazyRouteComponent(RetailMediaSeiten, 'RetailMediaEinbindung') });

/** DXL-Ansicht eines Marktplatzes in einem neuen Tab – die Seite legt die Ansicht selbst an (routes/AnsichtStart.tsx) */
const AnsichtStartSeite = lazyRouteComponent(() => import('./routes/AnsichtStart'), 'AnsichtStart');
const ansichtStart = createRoute({
  getParentRoute: () => root,
  path: '/ansicht/$mandantId',
  component: function AnsichtStartRoute() {
    const { mandantId } = ansichtStart.useParams();
    return <AnsichtStartSeite mandantId={mandantId} />;
  },
});

/** Öffentliche Startseite: stellt DXL Commerce vor und führt zur Registrierung */
const willkommen = createRoute({
  getParentRoute: () => root,
  path: '/willkommen',
  beforeLoad: ({ context }) => nichtAngemeldet(context.queryClient),
  component: lazyRouteComponent(() => import('./routes/Willkommen'), 'Willkommen'),
});

/** Öffentliche Seiten zu einzelnen Funktionen */
const funktionRetailMedia = createRoute({
  getParentRoute: () => root,
  path: '/funktionen/retail-media',
  component: lazyRouteComponent(() => import('./routes/VorstellungRetailMedia'), 'VorstellungRetailMedia'),
});
const funktionKi = createRoute({
  getParentRoute: () => root,
  path: '/funktionen/ki-produktdaten',
  component: lazyRouteComponent(() => import('./routes/VorstellungKi'), 'VorstellungKi'),
});

/** Rest der früheren IONOS-Seite (Server leert dabei den Browser-Cache): ohne Netzanfrage auf die Startseite */
const defaultsite = createRoute({
  getParentRoute: () => root,
  path: '/defaultsite',
  beforeLoad: () => {
    throw redirect({ to: '/', replace: true });
  },
});

/** Bestätigungslink aus der E-Mail – bewusst ohne Wächter (legt an und meldet an) */
const registrierungBestaetigen = createRoute({
  getParentRoute: () => root,
  path: '/registrieren/bestaetigen/$token',
  component: lazyRouteComponent(() => import('./routes/Registrieren'), 'RegistrierungBestaetigen'),
});

/** Ankauf: Anfrageformular und Kundenseite (persönlicher Link) im Branding des Marktplatzes – ohne Anmeldung */
const ankaufFormular = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/ankauf',
  component: lazyRouteComponent(() => import('./routes/AnkaufFormular'), 'AnkaufFormular'),
});

const ankaufKunde = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/ankauf/anfrage',
  validateSearch: (s: Record<string, unknown>): { z?: string } => ({ z: typeof s.z === 'string' ? s.z : undefined }),
  component: lazyRouteComponent(() => import('./routes/AnkaufKunde'), 'AnkaufKunde'),
});

/** Verkäuferportal: Anmeldung per Einmal-Link (?t=…), eigene Anfragen, Angebote, Rückfragen – ohne Konto im Tool */
const ankaufPortal = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/ankauf/konto',
  validateSearch: (s: Record<string, unknown>): { t?: string; a?: string } => ({
    t: typeof s.t === 'string' ? s.t : undefined,
    a: typeof s.a === 'string' ? s.a : undefined,
  }),
  component: lazyRouteComponent(() => import('./routes/AnkaufPortal'), 'AnkaufPortal'),
});

/** Retourenportal für Kunden (Gastzugang oder persönlicher Link ?z=…) im Branding des Marktplatzes – ohne Anmeldung */
const retoureKunde = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/retoure',
  validateSearch: (s: Record<string, unknown>): { z?: string } => ({ z: typeof s.z === 'string' ? s.z : undefined }),
  component: lazyRouteComponent(() => import('./routes/RetoureKunde'), 'RetoureKunde'),
});

/** Einladung annehmen / Passwort zurücksetzen – bewusst ohne Wächter */
const zugang = createRoute({
  getParentRoute: () => root,
  path: '/zugang/$token',
  component: lazyRouteComponent(() => import('./routes/Zugang'), 'Zugang'),
});

const code = createRoute({
  getParentRoute: () => root,
  path: '/anmelden/code',
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meAbfrage);
    if (!me) throw redirect({ to: '/anmelden' });
    if (me.stufe === 'voll') throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Code'), 'Code'),
});

const zweiFaktor = createRoute({
  getParentRoute: () => root,
  path: '/zwei-faktor',
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meAbfrage);
    if (!me) throw redirect({ to: '/anmelden' });
    if (me.stufe !== 'voll') throw redirect({ to: '/anmelden/code' });
  },
  component: lazyRouteComponent(() => import('./routes/ZweiFaktor'), 'ZweiFaktor'),
});

/* ---------------------------------------------------------------- Händlerportal (eigene Sitzung) */

type PortalSeite = 'PortalBestaetigen' | 'PortalPasswortVergessen' | 'PortalNeuesPasswort' | 'PortalCode';
const portalSeite = (name: PortalSeite) => lazyRouteComponent(() => import('./routes/portal/PortalZugang'), name);

/** Angemeldeter Bereich: Rahmen mit Navigation, Seiten darin über <Outlet/> */
const portalRahmen = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/portal',
  beforeLoad: async ({ context, params }) => {
    const me = await context.queryClient.ensureQueryData(portalMeAbfrage(params.kuerzel));
    if (!me) throw redirect({ to: '/m/$kuerzel/portal/anmelden', params, search: {} });
    if (me.stufe !== 'voll') throw redirect({ to: '/m/$kuerzel/portal/code', params });
  },
  component: lazyRouteComponent(() => import('./routes/portal/PortalRahmen'), 'PortalRahmen'),
});

/** Startseite: nach der Freigabe das Dashboard, vorher (und für Marken) die Kontoseite */
const portalStart = createRoute({
  getParentRoute: () => portalRahmen,
  path: '/',
  component: lazyRouteComponent(() => import('./routes/portal/PortalUebersicht'), 'PortalStart'),
});
const portalKonto = createRoute({ getParentRoute: () => portalRahmen, path: '/konto', component: lazyRouteComponent(() => import('./routes/portal/PortalKonto'), 'PortalKonto') });

const portalProdukte = createRoute({
  getParentRoute: () => portalRahmen,
  path: '/produkte',
  validateSearch: (s: Record<string, unknown>): { p?: string; neu?: boolean } => ({
    p: typeof s.p === 'string' ? s.p : undefined,
    neu: s.neu === true || s.neu === 'true' ? true : undefined,
  }),
  component: lazyRouteComponent(() => import('./routes/portal/PortalProdukte'), 'PortalProdukte'),
});

const portalBestellungen = createRoute({ getParentRoute: () => portalRahmen, path: '/bestellungen', component: lazyRouteComponent(() => import('./routes/portal/PortalBestellungen'), 'PortalBestellungen') });
/** Retouren D4: Rücksendungen an den Händler (Mails verlinken auf /m/<kürzel>/portal/retouren); ?paket=<id> öffnet ein Paket */
const portalRetouren = createRoute({
  getParentRoute: () => portalRahmen,
  path: '/retouren',
  validateSearch: (s: Record<string, unknown>): { paket?: string } => ({ paket: typeof s.paket === 'string' && /^[0-9a-f-]{36}$/i.test(s.paket) ? s.paket : undefined }),
  component: lazyRouteComponent(() => import('./routes/portal/PortalRetourenBereich'), 'PortalRetourenBereich'),
});
const portalAuszahlungen = createRoute({ getParentRoute: () => portalRahmen, path: '/auszahlungen', component: lazyRouteComponent(() => import('./routes/portal/PortalAuszahlungen'), 'PortalAuszahlungen') });
const portalAnbindungen = createRoute({ getParentRoute: () => portalRahmen, path: '/anbindungen', component: lazyRouteComponent(() => import('./routes/portal/PortalAnbindungen'), 'PortalAnbindungen') });
const portalTeam = createRoute({ getParentRoute: () => portalRahmen, path: '/team', component: lazyRouteComponent(() => import('./routes/portal/PortalTeam'), 'PortalTeam') });
const portalVertraege = createRoute({ getParentRoute: () => portalRahmen, path: '/vertraege', component: lazyRouteComponent(() => import('./routes/portal/PortalVertraege'), 'PortalVertraege') });
const portalWerbung = createRoute({
  getParentRoute: () => portalRahmen,
  path: '/werbung',
  // Rückkehr vom Bezahlen: ?aufladung=<id> → Stand abgleichen, ?zahlungsmittel=1 → Hinterlegen abgleichen (routes/portal/PortalWerbung.tsx)
  // ohne bereich die Übersicht; ?bereich=guthaben|kampagnen|werbemittel|berichte wählt den Reiter, ?k=<id> öffnet eine Kampagne,
  // ?neu legt eine an, ?w=<id> öffnet ein Werbemittel
  validateSearch: (s: Record<string, unknown>): { aufladung?: string; zahlungsmittel?: string; bereich?: 'guthaben' | 'kampagnen' | 'werbemittel' | 'berichte' | 'produktgruppen'; k?: string; w?: string; neu?: boolean } => ({
    aufladung: typeof s.aufladung === 'string' ? s.aufladung : undefined,
    zahlungsmittel: typeof s.zahlungsmittel === 'string' || typeof s.zahlungsmittel === 'number' ? String(s.zahlungsmittel) : undefined,
    bereich: s.bereich === 'guthaben' || s.bereich === 'kampagnen' || s.bereich === 'werbemittel' || s.bereich === 'berichte' || s.bereich === 'produktgruppen' ? s.bereich : undefined,
    k: typeof s.k === 'string' ? s.k : undefined,
    w: typeof s.w === 'string' ? s.w : undefined,
    neu: s.neu === true || s.neu === 'true' ? true : undefined,
  }),
  component: lazyRouteComponent(() => import('./routes/portal/PortalWerbung'), 'PortalWerbung'),
});

/** Retail Media RM-9: Startseite per WKZ buchen */
const portalStartseite = createRoute({ getParentRoute: () => portalRahmen, path: '/startseite', component: lazyRouteComponent(() => import('./routes/portal/PortalWkz'), 'PortalWkz') });

const portalAnmelden = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/portal/anmelden',
  validateSearch: (s: Record<string, unknown>): { registriert?: boolean } => ({ registriert: s.registriert === true || s.registriert === 'true' ? true : undefined }),
  beforeLoad: async ({ context, params }) => {
    const me = await context.queryClient.ensureQueryData(portalMeAbfrage(params.kuerzel));
    if (me?.stufe === 'voll') throw redirect({ to: '/m/$kuerzel/portal', params });
  },
  component: lazyRouteComponent(() => import('./routes/portal/PortalAnmelden'), 'PortalAnmelden'),
});

const portalRegistrieren = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/portal/registrieren',
  component: lazyRouteComponent(() => import('./routes/portal/PortalRegistrieren'), 'PortalRegistrieren'),
});

const portalBestaetigen = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/portal/bestaetigen/$token',
  component: portalSeite('PortalBestaetigen'),
});

const portalVergessen = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/portal/passwort-vergessen',
  component: portalSeite('PortalPasswortVergessen'),
});

const portalZugang = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/portal/zugang/$token',
  component: portalSeite('PortalNeuesPasswort'),
});

const portalCode = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/portal/code',
  beforeLoad: async ({ context, params }) => {
    const me = await context.queryClient.ensureQueryData(portalMeAbfrage(params.kuerzel));
    if (!me) throw redirect({ to: '/m/$kuerzel/portal/anmelden', params, search: {} });
    if (me.stufe === 'voll') throw redirect({ to: '/m/$kuerzel/portal', params });
  },
  component: portalSeite('PortalCode'),
});

/** Portal-Ansicht aus dem Marktplatz-Konto: Einmal-Link einlösen (sso_einloesen) */
const portalSso = createRoute({
  getParentRoute: () => root,
  path: '/m/$kuerzel/portal/sso/$token',
  component: lazyRouteComponent(() => import('./routes/portal/PortalSso'), 'PortalSso'),
});

/* ---------------------------------------------------------------- angemeldet */

const app = createRoute({
  getParentRoute: () => root,
  id: '_app',
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meAbfrage);
    if (!me) {
      const { offen } = await context.queryClient.ensureQueryData(einrichtungAbfrage);
      // Startseite ohne Anmeldung: Vorstellung von DXL Commerce statt direkt zum Login
      throw redirect({ to: offen ? '/einrichtung' : location.pathname === '/' ? '/willkommen' : '/anmelden', replace: true });
    }
    if (me.stufe !== 'voll') throw redirect({ to: '/anmelden/code' });
    if (me.zweiFaktorEinrichten) throw redirect({ to: '/zwei-faktor' });
    return { me };
  },
  component: Shell,
});

const start = createRoute({
  getParentRoute: () => app,
  path: '/',
  beforeLoad: ({ context }) => {
    const { me } = context;
    if (!me.mandant) {
      // Plattform: erster Bereich, den das Rechte-Level erlaubt (in der Regel Marktplätze)
      const erster = navigationFuer(me)[0]?.punkte[0];
      throw redirect(erster ? navZiel(erster) : { to: '/konto' });
    }
    if (me.nutzer.rechte.view_dashboard) throw redirect({ to: '/uebersicht' });
    // ohne Übersicht: erster erlaubter Bereich
    const erster = navigationFuer(me)[0]?.punkte[0];
    if (erster) throw redirect(navZiel(erster));
    throw redirect({ to: '/konto' });
  },
});

const uebersicht = createRoute({
  getParentRoute: () => app,
  path: '/uebersicht',
  beforeLoad: ({ context }) => {
    if (!context.me.mandant || !context.me.nutzer.rechte.view_dashboard) throw redirect({ to: '/konto' });
  },
  component: lazyRouteComponent(() => import('./routes/Uebersicht'), 'Uebersicht'),
});

const nutzer = createRoute({
  getParentRoute: () => app,
  path: '/nutzer',
  validateSearch: (s: Record<string, unknown>): { id?: string; neu?: boolean } => ({
    id: typeof s.id === 'string' ? s.id : undefined,
    neu: s.neu === true || s.neu === 'true' ? true : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!context.me.mandant || !context.me.nutzer.rechte.users) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Nutzer'), 'Nutzer'),
});

const haendler = createRoute({
  getParentRoute: () => app,
  path: '/haendler',
  // ?h=<id> öffnet die Angaben eines Händlers, ?status= setzt den Filter (z. B. aus der Übersicht)
  validateSearch: (s: Record<string, unknown>): { h?: string; status?: HaendlerStatus } => ({
    h: typeof s.h === 'string' ? s.h : undefined,
    status: (HAENDLER_STATUS as readonly unknown[]).includes(s.status) ? (s.status as HaendlerStatus) : undefined,
  }),
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.marketplace || !m.module.includes('marktplatz')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Haendler'), 'Haendler'),
});

const produkte = createRoute({
  getParentRoute: () => app,
  path: '/produkte',
  // ?p=<id> öffnet ein Produkt, ?haendler=<id> filtert (z. B. aus den Händler-Kennzahlen), ?status= wählt die Kachel
  // ?b=<id> öffnet den Editor, ?anr=1 dort gleich „Fehlende Felder mit KI anreichern“
  validateSearch: (s: Record<string, unknown>): { p?: string; b?: string; anr?: boolean; haendler?: string; status?: ProduktStatus } => ({
    p: typeof s.p === 'string' ? s.p : undefined,
    b: typeof s.b === 'string' ? s.b : undefined,
    anr: s.anr === true || s.anr === 'true' || s.anr === 1 ? true : undefined,
    haendler: typeof s.haendler === 'string' ? s.haendler : undefined,
    status: (PRODUKT_STATUS as readonly unknown[]).includes(s.status) ? (s.status as ProduktStatus) : undefined,
  }),
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.marketplace || !m.module.includes('marktplatz')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Produkte'), 'Produkte'),
});

const produktfelder = createRoute({
  getParentRoute: () => app,
  path: '/produktfelder',
  // ?bereich= wählt den Reiter (Felder, Kategorien, Qualität)
  validateSearch: (s: Record<string, unknown>): { bereich?: 'felder' | 'kategorien' | 'qualitaet' } => ({
    bereich: s.bereich === 'felder' || s.bereich === 'kategorien' || s.bereich === 'qualitaet' ? s.bereich : undefined,
  }),
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.marketplace || !m.module.includes('marktplatz')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Produktfelder'), 'Produktfelder'),
});

const bestellungen = createRoute({
  getParentRoute: () => app,
  path: '/bestellungen',
  // ?b=<id> öffnet eine Bestellung, ?haendler=<id> filtert (z. B. aus den Händler-Kennzahlen)
  validateSearch: (s: Record<string, unknown>): { b?: string; haendler?: string } => ({
    b: typeof s.b === 'string' ? s.b : undefined,
    haendler: typeof s.haendler === 'string' ? s.haendler : undefined,
  }),
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.marketplace || !m.module.includes('marktplatz')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Bestellungen'), 'Bestellungen'),
});

const auszahlungen = createRoute({
  getParentRoute: () => app,
  path: '/auszahlungen',
  // ?a=<id> öffnet eine Auszahlung, ?haendler=<id> filtert
  validateSearch: (s: Record<string, unknown>): { a?: string; haendler?: string } => ({
    a: typeof s.a === 'string' ? s.a : undefined,
    haendler: typeof s.haendler === 'string' ? s.haendler : undefined,
  }),
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.payouts || !m.module.includes('marktplatz')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Auszahlungen'), 'Auszahlungen'),
});

const channels = createRoute({
  getParentRoute: () => app,
  path: '/channels',
  // ?k=<id> öffnet einen Kanal, ?neu=1 den Katalog, ?prompts=1 die Prompt-Bibliothek
  validateSearch: (s: Record<string, unknown>): { k?: string; neu?: boolean; prompts?: boolean } => ({
    k: typeof s.k === 'string' ? s.k : undefined,
    neu: s.neu === true || s.neu === 'true' || s.neu === 1 ? true : undefined,
    prompts: s.prompts === true || s.prompts === 'true' || s.prompts === 1 ? true : undefined,
  }),
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.channels || !m.module.includes('marktplatz')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Channels'), 'Channels'),
});

const ankauf = createRoute({
  getParentRoute: () => app,
  path: '/ankauf',
  // ?a=<id> öffnet eine Anfrage (Link in den Team-Mails), ?einstellungen=1 die Einstellungen
  validateSearch: (s: Record<string, unknown>): { a?: string; einstellungen?: boolean } => ({
    a: typeof s.a === 'string' ? s.a : undefined,
    einstellungen: s.einstellungen === true || s.einstellungen === 'true' || s.einstellungen === 1 ? true : undefined,
  }),
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !(context.me.nutzer.rechte.ankauf || context.me.nutzer.rechte.ankauf_lesen) || !m.module.includes('ankauf')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Ankauf'), 'Ankauf'),
});

const erpErlaubt = ({ context }: { context: { me: Me } }) => {
  const m = context.me.mandant;
  if (!m || !context.me.nutzer.rechte.erp || !m.module.includes('erp')) throw redirect({ to: '/' });
};

// ERP-Connector: Start mit den verbundenen ERPs und der Auswahl (?neu=1 öffnet „ERP verbinden“)
const erp = createRoute({
  getParentRoute: () => app,
  path: '/erp',
  validateSearch: (s: Record<string, unknown>): { neu?: boolean } => ({ neu: s.neu === true || s.neu === 'true' || s.neu === 1 ? true : undefined }),
  beforeLoad: erpErlaubt,
  component: lazyRouteComponent(() => import('./routes/Erp'), 'ErpStart'),
});

// ein ERP: ?bereich=<Reiter> (verbindung, produkte, …, protokoll, fehler); nur Systeme mit Adapter
const erpSystem = createRoute({
  getParentRoute: () => app,
  path: '/erp/$system',
  validateSearch: (s: Record<string, unknown>): { bereich?: ErpReiter } => ({
    bereich: typeof s.bereich === 'string' && (ERP_REITER_IDS as readonly string[]).includes(s.bereich) ? (s.bereich as ErpReiter) : undefined,
  }),
  beforeLoad: ({ context, params }) => {
    erpErlaubt({ context });
    if (!istErpSystem(params.system)) throw redirect({ to: '/erp', search: {} });
  },
  component: lazyRouteComponent(() => import('./routes/Erp'), 'Erp'),
});

const ankaufKatalog = createRoute({
  getParentRoute: () => app,
  path: '/ankauf/katalog',
  // ?e=<id> bzw. ?e=neu öffnet einen Eintrag, ?import=1 den CSV-Import
  validateSearch: (s: Record<string, unknown>): { e?: string; import?: boolean } => ({
    e: typeof s.e === 'string' ? s.e : undefined,
    import: s.import === true || s.import === 'true' || s.import === 1 ? true : undefined,
  }),
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !(context.me.nutzer.rechte.ankauf || context.me.nutzer.rechte.ankauf_lesen) || !m.module.includes('ankauf')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/AnkaufKatalog'), 'AnkaufKatalog'),
});

const werbekonten = createRoute({
  getParentRoute: () => app,
  path: '/werbekonten',
  // ?k=<id> öffnet ein Werbekonto, ?w=<id> ein Werbemittel zur Prüfung, ?neu=1 das Anlegen, ?einladen=1 „Marke einladen“ (RM-8)
  validateSearch: (s: Record<string, unknown>): { k?: string; w?: string; neu?: boolean; einladen?: boolean } => ({
    k: typeof s.k === 'string' ? s.k : undefined,
    w: typeof s.w === 'string' ? s.w : undefined,
    neu: s.neu === true || s.neu === 'true' || s.neu === 1 ? true : undefined,
    einladen: s.einladen === true || s.einladen === 'true' || s.einladen === 1 ? true : undefined,
  }),
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.marketplace || !m.module.includes('werbung')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Werbekonten'), 'Werbekonten'),
});

const kampagnen = createRoute({
  getParentRoute: () => app,
  path: '/kampagnen',
  // ?k=<id> öffnet eine Kampagne, ?werbekonto=<id> filtert auf ein Werbekonto, ?format=<typ> auf ein Format
  validateSearch: (s: Record<string, unknown>): { k?: string; werbekonto?: string; format?: KampagneTyp } => ({
    k: typeof s.k === 'string' ? s.k : undefined,
    werbekonto: typeof s.werbekonto === 'string' ? s.werbekonto : undefined,
    format: (KAMPAGNE_TYP_LISTE as readonly string[]).includes(s.format as string) ? (s.format as KampagneTyp) : undefined,
  }),
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.marketplace || !m.module.includes('werbung')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Kampagnen'), 'Kampagnen'),
});

const platzierungen = createRoute({
  getParentRoute: () => app,
  path: '/platzierungen',
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.admin || !m.module.includes('werbung')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Platzierungen'), 'Platzierungen'),
});

// Klick auf die Überschrift „Retail Media“: Übersicht über Werbeumsatz, Kampagnen, Werbekonten und offene Aufgaben
const werbungUebersicht = createRoute({
  getParentRoute: () => app,
  path: '/werbung',
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.marketplace || !m.module.includes('werbung')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/WerbungUebersicht'), 'WerbungUebersicht'),
});

const werbungBerichte = createRoute({
  getParentRoute: () => app,
  path: '/werbung/berichte',
  // ?werbekonto=<id> und ?kampagne=<id> filtern den Bericht
  validateSearch: (s: Record<string, unknown>): { werbekonto?: string; kampagne?: string } => ({
    werbekonto: typeof s.werbekonto === 'string' ? s.werbekonto : undefined,
    kampagne: typeof s.kampagne === 'string' ? s.kampagne : undefined,
  }),
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.marketplace || !m.module.includes('werbung')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/WerbungBerichte'), 'WerbungBerichte'),
});

const wkzBuchungen = createRoute({
  getParentRoute: () => app,
  path: '/werbung/wkz',
  // ?b=<id> öffnet eine Buchung, ?neu=1 das Buchen für ein Werbekonto
  validateSearch: (s: Record<string, unknown>): { b?: string; neu?: boolean } => ({
    b: typeof s.b === 'string' ? s.b : undefined,
    neu: s.neu === true || s.neu === 'true' || s.neu === 1 ? true : undefined,
  }),
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.marketplace || !m.module.includes('werbung')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/WkzBuchungen'), 'WkzBuchungen'),
});

// Werbemittel gehören zum Werbekonto (Detail unter /werbekonten) – die frühere Liste leitet dorthin weiter
const werbungWerbemittel = createRoute({
  getParentRoute: () => app,
  path: '/werbung/werbemittel',
  beforeLoad: () => {
    throw redirect({ to: '/werbekonten' });
  },
});

const werbungEinbindung = createRoute({
  getParentRoute: () => app,
  path: '/werbung/einbindung',
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.marketplace || !m.module.includes('werbung')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/WerbungEinbindung'), 'WerbungEinbindung'),
});

/** Retail Media E1: Marken-Zuordnung (Markenwerte, GS1-Präfixe, Vorschläge) und Bewerbbarkeits-Regeln */
const werbungMarkenZuordnung = createRoute({
  getParentRoute: () => app,
  path: '/werbung/marken-zuordnung',
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.marketplace || !m.module.includes('werbung')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/WerbungMarkenZuordnung'), 'WerbungMarkenZuordnung'),
});

const einstellungen = createRoute({
  getParentRoute: () => app,
  path: '/einstellungen',
  // Marktplatz und Plattform haben eigene Reiter – ein Wert, der im jeweiligen Bereich fehlt, zeigt den ersten Reiter
  validateSearch: (s: Record<string, unknown>): { bereich?: EinstellungBereichSuche } => ({
    bereich: typeof s.bereich === 'string' && (EINSTELLUNG_BEREICHE_SUCHE as readonly string[]).includes(s.bereich) ? (s.bereich as EinstellungBereichSuche) : undefined,
  }),
  beforeLoad: ({ context }) => {
    // Marktplatz: Recht Verwaltung; Plattform: Nutzer der Plattform (Reiter je nach Rechte-Level)
    const { me } = context;
    if (me.mandant ? !me.nutzer.rechte.admin : !darfPlattform(me, 'marktplaetze_lesen')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Einstellungen'), 'Einstellungen'),
});

const konto = createRoute({
  getParentRoute: () => app,
  path: '/konto',
  component: lazyRouteComponent(() => import('./routes/Konto'), 'Konto'),
});

/** Startseite der DXL-Plattform: Kennzahlen über alle Marktplätze */
const plattformUebersicht = createRoute({
  getParentRoute: () => app,
  path: '/plattform',
  beforeLoad: ({ context }) => {
    if (context.me.mandant || !darfPlattform(context.me, 'marktplaetze_lesen')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/PlattformUebersicht'), 'PlattformUebersicht'),
});

/** Plattform → Retail Media: Dashboard über alle Marktplätze; ?m=<id> öffnet das Dashboard eines Marktplatzes */
const plattformWerbung = createRoute({
  getParentRoute: () => app,
  path: '/plattform/werbung',
  validateSearch: (s: Record<string, unknown>): { m?: string } => ({ m: typeof s.m === 'string' ? s.m : undefined }),
  beforeLoad: ({ context }) => {
    if (context.me.mandant || !darfPlattform(context.me, 'marktplaetze_lesen')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/PlattformWerbung'), 'PlattformWerbung'),
});

const marktplaetze = createRoute({
  getParentRoute: () => app,
  path: '/marktplaetze',
  // ?m=<id> öffnet die Details, ?neu=1 das Anlegen – beides verlinkbar (z. B. aus der Befehlspalette)
  validateSearch: (s: Record<string, unknown>): { m?: string; neu?: boolean } => ({
    m: typeof s.m === 'string' ? s.m : undefined,
    neu: s.neu === true || s.neu === 'true' || s.neu === 1 ? true : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (context.me.mandant || !darfPlattform(context.me, 'marktplaetze_lesen')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Marktplaetze'), 'Marktplaetze'),
});

const pakete = createRoute({
  getParentRoute: () => app,
  path: '/pakete',
  // ?bereich= wählt den Reiter; ?r= Rechnung, ?m= Konditionen eines Marktplatzes, ?p= Paket (oder „neu“)
  validateSearch: (s: Record<string, unknown>): { bereich?: 'rechnungen' | 'marktplaetze' | 'pakete' | 'addons' | 'guthaben' | 'rechnungsdaten' | 'ki'; r?: string; m?: string; p?: string } => ({
    bereich: s.bereich === 'rechnungen' || s.bereich === 'marktplaetze' || s.bereich === 'pakete' || s.bereich === 'addons' || s.bereich === 'guthaben' || s.bereich === 'rechnungsdaten' || s.bereich === 'ki' ? s.bereich : undefined,
    r: typeof s.r === 'string' ? s.r : undefined,
    m: typeof s.m === 'string' ? s.m : undefined,
    p: typeof s.p === 'string' ? s.p : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (context.me.mandant || !darfPlattform(context.me, 'abrechnung_lesen')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Pakete'), 'Pakete'),
});

const provision = createRoute({
  getParentRoute: () => app,
  path: '/provision',
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.admin || !m.module.includes('marktplatz')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Provision'), 'Provision'),
});

const shop = createRoute({
  getParentRoute: () => app,
  path: '/shop',
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    // RM-8: auch Standalone-Shops (Retail Media ohne Marktplatz) verbinden hier ihren Shop für den Katalog-Import
    if (!m || !context.me.nutzer.rechte.admin || !(m.module.includes('marktplatz') || m.module.includes('werbung'))) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Shop'), 'Shop'),
});

const buybox = createRoute({
  getParentRoute: () => app,
  path: '/buybox',
  beforeLoad: ({ context }) => {
    const m = context.me.mandant;
    if (!m || !context.me.nutzer.rechte.marketplace || !m.module.includes('marktplatz')) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/BuyBox'), 'BuyBox'),
});

const paket = createRoute({
  getParentRoute: () => app,
  path: '/paket',
  validateSearch: (s: Record<string, unknown>): { aufladen?: boolean; checkout?: 'erfolg' | 'abbruch'; guthaben?: 'erfolg' | 'abbruch' } => ({
    aufladen: s.aufladen === true || s.aufladen === 'true' ? true : undefined,
    checkout: s.checkout === 'erfolg' || s.checkout === 'abbruch' ? s.checkout : undefined,
    guthaben: s.guthaben === 'erfolg' || s.guthaben === 'abbruch' ? s.guthaben : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!context.me.mandant || !context.me.nutzer.rechte.admin) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Paket'), 'Paket'),
});

const rechnungen = createRoute({
  getParentRoute: () => app,
  path: '/rechnungen',
  beforeLoad: ({ context }) => {
    if (!context.me.mandant || !context.me.nutzer.rechte.admin) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Rechnungen'), 'Rechnungen'),
});

const anleitung = createRoute({
  getParentRoute: () => app,
  path: '/anleitung',
  // Marktplatz-Konto: für alle Nutzer; Plattform: Anleitung für DXL-Admins
  beforeLoad: ({ context }) => {
    if (!context.me.mandant && context.me.nutzer.rolle !== 'dxl_admin') throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/Anleitung'), 'Anleitung'),
});

const baum = root.addChildren([
  einrichtung,
  ansichtStart,
  defaultsite,
  willkommen,
  funktionRetailMedia,
  funktionKi,
  anmelden,
  mandantAnmelden,
  passwortVergessen,
  mandantPasswortVergessen,
  registrieren,
  retailMedia,
  retailMediaFormate,
  retailMediaMarken,
  retailMediaHaendler,
  retailMediaAuktion,
  retailMediaEinbindung,
  registrierungBestaetigen,
  ankaufFormular,
  ankaufKunde,
  ankaufPortal,
  retoureKunde,
  zugang,
  code,
  zweiFaktor,
  portalRahmen.addChildren([portalStart, portalKonto, portalProdukte, portalBestellungen, portalRetouren, portalAuszahlungen, portalAnbindungen, portalTeam, portalVertraege, portalWerbung, portalStartseite]),
  portalAnmelden,
  portalRegistrieren,
  portalBestaetigen,
  portalVergessen,
  portalZugang,
  portalCode,
  portalSso,
  app.addChildren([start, uebersicht, plattformUebersicht, plattformWerbung, marktplaetze, pakete, nutzer, haendler, produkte, produktfelder, bestellungen, auszahlungen, channels, ankauf, ankaufKatalog, erp, erpSystem, werbungUebersicht, werbekonten, kampagnen, werbungWerbemittel, platzierungen, wkzBuchungen, werbungEinbindung, werbungMarkenZuordnung, werbungBerichte, einstellungen, paket, rechnungen, provision, buybox, shop, anleitung, konto]),
]);

export function erzeugeRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree: baum,
    context: { queryClient },
    defaultPreload: 'intent', // Module beim Überfahren vorladen
    defaultPreloadStaleTime: 0,
    // Nie eine leere Seite: Fehler beim Laden einer Seite zeigen und neu laden anbieten
    defaultErrorComponent: SeitenFehler,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof erzeugeRouter>;
  }
}
